const FACETS_AVAILABILITY_PARAM = 'filter.v.availability';
const FACETS_AVAILABILITY_VALUE = '1';
const FACETS_STORAGE_MANUAL = 'facets.availabilityManual';
const FACETS_STORAGE_AUTO = 'facets.availabilityAuto';

/**
 * Size option filters use params like filter.v.option.size, filter.v.option.us-size,
 * or filter.v.option.us size (Shopify may keep a space in the option name).
 */
function isSizeFilterParam(paramName) {
  if (!paramName || !paramName.startsWith('filter.v.option.')) return false;
  return paramName.slice('filter.v.option.'.length).toLowerCase().includes('size');
}

/**
 * Keep each size value as its own param entry (OR). Never coerce values (e.g. keep "00" ≠ "0").
 * @param {URLSearchParams} searchParams
 * @returns {URLSearchParams}
 */
function ensureExactSizeOrParams(searchParams) {
  const sizeEntries = [];

  for (const [key, value] of searchParams.entries()) {
    if (!isSizeFilterParam(key)) continue;
    // Split accidental comma-joined values into separate OR params; keep exact string case
    String(value).split(',').forEach((part) => {
      const exact = part; // do not trim/number/lowercase — "00" must stay "00"
      if (exact !== '') {
        sizeEntries.push([key, exact]);
      }
    });
  }

  if (!sizeEntries.length) return searchParams;

  // Remove existing size keys, then re-append each exact value separately (OR)
  const sizeKeys = [...new Set(sizeEntries.map(([key]) => key))];
  sizeKeys.forEach((key) => searchParams.delete(key));
  sizeEntries.forEach(([key, value]) => {
    searchParams.append(key, value);
  });

  return searchParams;
}

/**
 * Build facet query params from a form, preserving exact checkbox string values.
 * Multiple checked values for the same name become repeated params (Shopify OR).
 * @param {HTMLFormElement} form
 * @returns {URLSearchParams}
 */
function buildFacetSearchParams(form) {
  const searchParams = new URLSearchParams();

  Array.from(form.elements).forEach((element) => {
    if (!element.name || element.disabled) return;

    const type = (element.type || '').toLowerCase();
    if (type === 'submit' || type === 'button' || type === 'file' || type === 'reset') return;

    if (type === 'checkbox' || type === 'radio') {
      if (!element.checked) return;
      // Exact string value from the input — never Number() / toLowerCase()
      searchParams.append(element.name, String(element.value));
      return;
    }

    if (element.tagName === 'SELECT' && element.multiple) {
      Array.from(element.selectedOptions).forEach((option) => {
        searchParams.append(element.name, String(option.value));
      });
      return;
    }

    searchParams.append(element.name, String(element.value));
  });

  return ensureExactSizeOrParams(searchParams);
}

function hasActiveSizeFilter(searchParams) {
  for (const key of searchParams.keys()) {
    if (isSizeFilterParam(key) && searchParams.getAll(key).some((value) => value !== '')) {
      return true;
    }
  }
  return false;
}

function hasActiveFilterParams(searchParams) {
  for (const key of searchParams.keys()) {
    if (key.startsWith('filter.')) return true;
  }
  return false;
}

function getAvailabilityFlag(key) {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch (e) {
    return false;
  }
}

function setAvailabilityFlag(key, enabled) {
  try {
    if (enabled) {
      sessionStorage.setItem(key, '1');
    } else {
      sessionStorage.removeItem(key);
    }
  } catch (e) {
    // sessionStorage may be unavailable
  }
}

function resetAvailabilityFlags() {
  setAvailabilityFlag(FACETS_STORAGE_MANUAL, false);
  setAvailabilityFlag(FACETS_STORAGE_AUTO, false);
}

/**
 * When sizes are selected, keep In stock enabled. When the last size is removed,
 * drop auto-applied availability unless the customer enabled it manually.
 * @param {URLSearchParams} searchParams
 * @param {{ source?: string, event?: Event }} [context]
 * @returns {URLSearchParams}
 */
function applySizeAvailabilityRules(searchParams, context = {}) {
  const { source, event } = context;
  const target = event && event.target;
  const isAvailabilityToggle =
    source === 'input' &&
    target &&
    target.name === FACETS_AVAILABILITY_PARAM;

  // Exact size strings + one param per value (OR) before availability rules
  ensureExactSizeOrParams(searchParams);

  if (isAvailabilityToggle) {
    if (target.checked) {
      setAvailabilityFlag(FACETS_STORAGE_MANUAL, true);
      setAvailabilityFlag(FACETS_STORAGE_AUTO, false);
    } else {
      setAvailabilityFlag(FACETS_STORAGE_MANUAL, false);
    }
  }

  const hasSize = hasActiveSizeFilter(searchParams);
  const hadAvailability = searchParams.get(FACETS_AVAILABILITY_PARAM) === FACETS_AVAILABILITY_VALUE;
  const isManual = getAvailabilityFlag(FACETS_STORAGE_MANUAL);
  const isAuto = getAvailabilityFlag(FACETS_STORAGE_AUTO);

  if (hasSize) {
    if (!hadAvailability) {
      searchParams.set(FACETS_AVAILABILITY_PARAM, FACETS_AVAILABILITY_VALUE);
      if (!isManual) {
        setAvailabilityFlag(FACETS_STORAGE_AUTO, true);
      }
    } else if (!isManual && !isAuto) {
      // Availability already in URL (shared link / prior session) — treat as manual to preserve
      setAvailabilityFlag(FACETS_STORAGE_MANUAL, true);
    }
    return searchParams;
  }

  // No sizes: remove auto-applied In stock; keep manual choice
  if (isAuto && !isManual) {
    searchParams.delete(FACETS_AVAILABILITY_PARAM);
    setAvailabilityFlag(FACETS_STORAGE_AUTO, false);
  }

  if (source === 'remove' && !hasActiveFilterParams(searchParams)) {
    resetAvailabilityFlags();
  }

  return searchParams;
}

/**
 *  @class
 *  @function FacetsToggle
 */
class FacetsToggle {

  constructor() {
    this.container = document.getElementById('Facet-Drawer');
    let button = document.getElementById('Facets-Toggle');

    // Add functionality to buttons
    if (button) {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        document.getElementsByTagName('body')[0].classList.toggle('open-cc');
        this.container.classList.toggle('active');
      });
    }
    setTimeout(function () {
      window.dispatchEvent(new Event('resize.resize-select'));
    }, 10);
  }
}


class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.querySelector('form').addEventListener('input', this.debouncedOnSubmit.bind(this));
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      console.log("onHistoryChange==>", event);
      const state = event.state || {};
      let searchParamsString = state.searchParams != null ? state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParamsString === FacetFiltersForm.searchParamsPrev) return;

      // Restore auto/manual flags from history when present so back/forward stays accurate
      if (state.searchParams != null) {
        setAvailabilityFlag(FACETS_STORAGE_AUTO, !!state.availabilityAuto);
        setAvailabilityFlag(FACETS_STORAGE_MANUAL, !!state.availabilityManual);
      }

      const searchParams = applySizeAvailabilityRules(new URLSearchParams(searchParamsString), { source: 'popstate' });
      searchParamsString = searchParams.toString();
      // If rules added availability, update the URL so back/forward stays in sync
      if (searchParamsString !== (state.searchParams != null ? state.searchParams : FacetFiltersForm.searchParamsInitial)) {
        FacetFiltersForm.renderPage(searchParamsString, null, true);
      } else {
        FacetFiltersForm.renderPage(searchParamsString, null, false);
      }
    };
    window.addEventListener('popstate', onHistoryChange);

    document.addEventListener('click', (event) => {
      const clearLink = event.target.closest('.mobile-filters-clear, .active-facets__button-remove');
      if (clearLink) {
        resetAvailabilityFlags();
      }
    });
  }

  static syncSizeAvailabilityOnLoad() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;

    const current = new URLSearchParams(window.location.search.slice(1));
    const before = current.toString();
    applySizeAvailabilityRules(current, { source: 'init' });
    const after = current.toString();

    if (after !== before) {
      FacetFiltersForm.renderPage(after, null, true);
    }
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const container = document.getElementsByClassName('thb-filter-count');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');

    for (var item of container) {
      item.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, {
          html,
          url
        }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);

        new FacetsToggle();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

    new FacetsToggle();
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;

    let top = document.getElementById('ProductGridContainer').getBoundingClientRect().top + document.documentElement.scrollTop - parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) - 30;

    window.scrollTo({
      top: top,
      left: 0,
      behavior: "smooth"
    });
  }

  static renderProductCount(html) {
    const countHtml = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove('loading');
      }
    }

  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm collapsible-row, #FacetFiltersFormMobile collapsible-row');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('collapsible-row') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`collapsible-row[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    new FacetsToggle();

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('collapsible-row'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-filter-count'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    console.log("updateURLHash==>", searchParams);
    history.pushState({
      searchParams,
      availabilityAuto: getAvailabilityFlag(FACETS_STORAGE_AUTO),
      availabilityManual: getAvailabilityFlag(FACETS_STORAGE_MANUAL)
    }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    // if(searchParams.includes('sort_by')) {
    //   window.sauleeAnalytics.trackSortByInteraction('sort_by');
    // }
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = event.target.closest('form');
    const searchParams = buildFacetSearchParams(form);
    if (searchParams.get('filter.v.price.gte') === "0.00") {
      searchParams.delete('filter.v.price.gte');
    }
    if (document.querySelector('.price_slider')) {
      if (searchParams.get('filter.v.price.lte') === parseFloat(document.querySelector('.price_slider').dataset.max).toFixed(2)) {
        searchParams.delete('filter.v.price.lte');
      }
    }

    applySizeAvailabilityRules(searchParams, { source: 'input', event });
    
    // Dispatch custom event if sort-by changed
    if (event.target.classList.contains('facet-filters__sort') || event.target.name === 'sort_by') {
      const sortByValue = searchParams.get('sort_by');
      if (sortByValue) {
        dispatchCustomEvent('sort-by:change', {
          value: sortByValue
        });
      }
    }
    
    // Dispatch custom event for filter changes
    if (event.target.name && event.target.name.startsWith('filter.')) {
      const filterName = event.target.name;
      const filterValue = event.target.value;
      const filterType = event.target.type;
      
      // Get filter category name from the collapsible row
      const collapsibleRow = event.target.closest('collapsible-row');
      let filterLabel = 'Unknown Filter';
      
      if (collapsibleRow) {
        // Try to get the label from summary.thb-filter-title
        const summaryElement = collapsibleRow.querySelector('summary.thb-filter-title');
        if (summaryElement) {
          filterLabel = summaryElement.textContent.trim();
        }
      }
      
      const isFilterDrawer = !!event.target.closest('#FacetFiltersFormMobile');
      
      // Get selected value label
      let filterValueLabel = filterValue;
      if (filterType === 'checkbox') {
        const label = event.target.closest('li')?.querySelector('label[for="' + event.target.id + '"]')?.textContent?.trim();
        if (label) {
          filterValueLabel = label;
        }
      }
      
      dispatchCustomEvent('filter:change', {
        filter_name: filterLabel,
        filter_value: filterValueLabel,
        filter_param: filterName,
        is_mobile: isFilterDrawer,
        checked: event.target.checked !== undefined ? event.target.checked : true
      });
    }
    
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    const searchParams = applySizeAvailabilityRules(new URLSearchParams(url), { source: 'remove', event });
    FacetFiltersForm.renderPage(searchParams.toString());
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.syncSizeAvailabilityOnLoad();


class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
      });
    });
  }
}

customElements.define('facet-remove', FacetRemove);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {

  constructor() {
    super();
  }
  connectedCallback() {
    let slider = this.querySelector('.price_slider'),
      amounts = this.querySelector('.price_slider_amount'),
      args = {
        start: [parseFloat(slider.dataset.minValue || 0), parseFloat(slider.dataset.maxValue || slider.dataset.max)],
        connect: true,
        step: 10,
        direction: document.dir,
        handleAttributes: [
          { 'aria-label': 'lower' },
          { 'aria-label': 'upper' },
        ],
        range: {
          'min': 0,
          'max': parseFloat(slider.dataset.max)
        }
      },
      event = new CustomEvent('input'),
      form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');

    if (slider.classList.contains('noUi-target')) {
      slider.noUiSlider.destroy();
    }
    noUiSlider.create(slider, args);

    slider.noUiSlider.on('update', function (values) {
      amounts.querySelector('.field__input_min').value = values[0];
      amounts.querySelector('.field__input_max').value = values[1];
    });
    slider.noUiSlider.on('change', function (values) {
      form.querySelector('form').dispatchEvent(event);
    });
  }
}
customElements.define('price-slider', PriceSlider);

window.addEventListener('load', () => {
  new FacetsToggle();
});